import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProgressBar from '../courses/ProgressBar';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import LoadingIndicator from '../common/LoadingIndicator';
import { useAuth } from '../contexts/AuthContext';

function CourseContent() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [userProgress, setUserProgress] = useState(0);
    const [completedSubCourses, setCompletedSubCourses] = useState({});
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchCourseAndProgress = async () => {
            setLoading(true);
            try {
                // Fetch course data
                const courseDoc = doc(db, 'courses', courseId);
                const courseData = await getDoc(courseDoc);

                if (courseData.exists()) {
                    const data = courseData.data();
                    // Ensure modules is always an array; convert object maps to arrays if needed
                    data.modules = Array.isArray(data.modules)
                        ? data.modules
                        : (data.modules && typeof data.modules === 'object')
                            ? Object.values(data.modules)
                            : [];
                    setCourse(data);

                    // Fetch completed subcourses
                    const completedRef = doc(db, 'users', user.uid, 'completedSubCourses', courseId);
                    const completedDoc = await getDoc(completedRef);
                    
                    if (completedDoc.exists()) {
                        const completedData = completedDoc.data().completed || {};
                        setCompletedSubCourses(completedData);

                        // Calculate progress with safe normalization and deduplication
                        const totalSubCourses = data.modules.reduce((total, module) => {
                            const subCoursesArray = Array.isArray(module?.subCourses)
                                ? module.subCourses
                                : (module?.subCourses && typeof module.subCourses === 'object')
                                    ? Object.values(module.subCourses)
                                    : [];
                            return total + subCoursesArray.length;
                        }, 0);

                        const uniqueCompletionKeys = new Set();
                        Object.keys(completedData).forEach((key) => {
                            if (typeof key === 'string' && key.length > 0) {
                                const [moduleIdPart, subCourseIdPart] = key.split('_');
                                if (moduleIdPart && subCourseIdPart) {
                                    uniqueCompletionKeys.add(`${moduleIdPart}_${subCourseIdPart}`);
                                }
                            }
                        });
                        const completedCount = uniqueCompletionKeys.size;

                        let progress = totalSubCourses > 0 ? (completedCount / totalSubCourses) * 100 : 0;
                        progress = Math.max(0, Math.min(100, progress));
                        setUserProgress(progress);
                    }
                } else {
                    console.error('Course does not exist');
                }
            } catch (error) {
                console.error('Error fetching course:', error);
            }
            setLoading(false);
        };

        if (user && courseId) {
            fetchCourseAndProgress();
        }
    }, [courseId, user]);

    const isSubCourseCompleted = (moduleId, subCourseId) => {
        const completionKey = `${moduleId}_${subCourseId}`;
        return completedSubCourses[completionKey] !== undefined;
    };

    const handleSubCourseClick = (moduleIndex, subCourseIndex) => {
        const module = course.modules[moduleIndex];
        const subCourse = module.subCourses[subCourseIndex];

        // Check for sequential completion
        if (moduleIndex === 0 && subCourseIndex === 0) {
            // Always allow access to the first sub-course
            navigate(`/courses/${courseId}/content/${module.moduleId}/${subCourse.subCourseId}`);
        } else {
            let previousSubCourseCompleted = false;
            if (subCourseIndex > 0) {
                // Check if the previous sub-course in the current module is completed
                const previousSubCourse = module.subCourses[subCourseIndex - 1];
                previousSubCourseCompleted = isSubCourseCompleted(module.moduleId, previousSubCourse.subCourseId);
            } else if (moduleIndex > 0) {
                // Check if the last sub-course of the previous module is completed
                const previousModule = course.modules[moduleIndex - 1];
                const lastSubCourseOfPreviousModule = previousModule.subCourses[previousModule.subCourses.length - 1];
                previousSubCourseCompleted = isSubCourseCompleted(previousModule.moduleId, lastSubCourseOfPreviousModule.subCourseId);
            }

            if (previousSubCourseCompleted) {
                navigate(`/courses/${courseId}/content/${module.moduleId}/${subCourse.subCourseId}`);
            } else {
                setShowModal(true);
            }
        }
    };

    if (loading) {
        return <LoadingIndicator />;
    }

    return (
        <div className="flex min-h-screen bg-gray-100">
            <div className="flex-1 flex flex-col">
                <header className="bg-white shadow-md p-4 flex justify-between items-center sticky top-0 z-10">

                    <h1 className="text-xl font-bold text-gray-800">{course?.title}</h1>
                </header>
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h2 className="text-lg font-bold mb-4">To complete the courses and earn your certificate, please follow these steps:</h2>
                    <ul className="list-disc pl-5 text-gray-700">
                        <li>Start with the First Course: You must complete courses in order.</li>
                        <li>Pass Each Evaluation: A minimum score of 75% is required to pass each course evaluation.</li>
                        <li>
                            Upon Submission, ensure you get "Results Submitted Successfully" before clicking Mark Complete.
                        </li>
                        <li>Check Your Progress: Completed courses will be marked with a tick on your schedule page.</li>
                        <li>Finish All Courses: Download your certificate after passing all courses.</li>
                    </ul>
                </div>

                <div className="p-6">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold mb-4">Course Progress</h2>
                        <ProgressBar progress={userProgress} />
                    </div>

                    <main className="space-y-8">
                        {Array.isArray(course?.modules) && course.modules.map((module, moduleIndex) => (
                            <div key={moduleIndex} className="bg-white p-4 rounded-lg shadow-md">
                                <h2 className="font-bold text-lg text-[rgb(130,88,18)]">
                                    {module.title || module.moduleId}
                                </h2>
                                <div className="ml-4 mt-2">
                                    {Array.isArray(module.subCourses) && module.subCourses.map((subCourse, subCourseIndex) => (
                                        <div key={subCourseIndex} 
                                             className="flex items-center justify-between my-2 p-2 bg-gray-50 rounded-md">
                                            <span
                                                className="font-normal cursor-pointer transform transition-transform duration-200 hover:scale-105 hover:text-iof"
                                                onClick={() => handleSubCourseClick(moduleIndex, subCourseIndex)}
                                            >
                                                {subCourse.title}
                                            </span>
                                            <div className="flex items-center">
                                                {isSubCourseCompleted(module.moduleId, subCourse.subCourseId) && (
                                                    <span className="text-green-600 mr-2">✓</span>
                                                )}
                                                <input
                                                    type="checkbox"
                                                    checked={isSubCourseCompleted(module.moduleId, subCourse.subCourseId)}
                                                    readOnly
                                                    className="ml-2"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </main>
                </div>
            </div>
            {/* Sequential Completion Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3 text-center">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">Action Required</h3>
                            <div className="mt-2 px-7 py-3">
                                <p className="text-sm text-gray-500">
                                    Please complete the previous sub-course before proceeding.
                                </p>
                            </div>
                            <div className="items-center px-4 py-3">
                                <button
                                    className="px-4 py-2 bg-[rgb(130,88,18)] text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-[rgb(110,68,0)] focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                                    onClick={() => setShowModal(false)}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CourseContent;